import classNames from "classnames";
import { useEffect, useState } from "react";
import { Mode } from "..";
import { BreakdownAccordion } from "../../../../../components/accordions/breakdownAccordion";
import { Button } from "../../../../../components/buttons/button";
import { InformationCard } from "../../../../../components/cards/infoCard";
import { NumberCard } from "../../../../../components/cards/numberCard";
import { FundCooperativeWalletModal } from "../../../../../components/modals/app_modals/finances/fundCooperativeWalletModal";
import { CooperativeWithdrawalModal } from "../../../../../components/modals/app_modals/finances/withdrawal";
import { TransactionDetailsModal } from "../../../../../components/modals/app_modals/transactionDetailsModal";
import { TableComponent } from "../../../../../components/table/table";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import { usePermissions } from "../../../../../customHooks/usePermissions";
import {
  formatToCurrency,
  getOffsetFromPage,
  getPageFromOffset,
  getTimeAgo,
} from "../../../../../helperFunctions";
import { SingleCooperativeDetails } from "../../../../../redux/features/cooperative/cooperative.type";
import {
  useGetCooperativeFinancesQuery,
  useGetCooperativeTransactionHistoryQuery,
} from "../../../../../redux/features/finances/finances.api";
import {
  CooperativeTransactionQuery,
  SingleCooperativeTransaction,
} from "../../../../../redux/features/finances/finances.type";
import { PermissionCode } from "../../../../../redux/features/settings/settings.type";
import { CooperativeTransactionWalletTypeMap } from "../../../../../utils/constants";

export const CooperativeFinances = ({
  cooperativeDetails,
  mode,
  permissions,
}: {
  cooperativeDetails: SingleCooperativeDetails;
  mode: Mode;
  permissions: PermissionCode[];
}) => {
  const [financesBreakdown, setFinancesBreakdown] = useState<
    { icon: string; title: string; value: number }[]
  >([]);
  const [query, setQuery] = useState<CooperativeTransactionQuery>({
    coop: cooperativeDetails.id,
    offset: 0,
    limit: 20,
    text: undefined,
    logs: true,
  });
  const [selectedTransaction, setSelectedTransaction] =
    useState<SingleCooperativeTransaction>();

  const {
    data: cooperativeFinances,
    isLoading: fetchingFiancesSummary,
    refetch: refetchSummary,
  } = useGetCooperativeFinancesQuery(
    {
      coop: cooperativeDetails.id,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: cooperativeTransactions,
    isFetching: fetchingTransactions,
    refetch: refetchTransactions,
  } = useGetCooperativeTransactionHistoryQuery(query, {
    refetchOnMountOrArgChange: true,
  });

  const { hasPermissionsFor } = usePermissions();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  useEffect(() => {
    if (cooperativeFinances?.data) {
      setFinancesBreakdown([
        {
          icon: "coins",
          title: "Contributions",
          value: cooperativeFinances?.data?.saving ?? 0,
        },
        {
          icon: "coins",
          title: "Deposits",
          value: cooperativeFinances?.data?.deposit ?? 0,
        },
        {
          icon: "coins",
          title: "Shares",
          value: cooperativeFinances?.data?.shares ?? 0,
        },
        {
          icon: "coins",
          title: "Loan Repayments",
          value: cooperativeFinances?.data?.repayment ?? 0,
        },
        {
          icon: "wallet",
          title: "Cooperative Fund Inflow",
          value: cooperativeFinances?.data?.wallet_payment ?? 0,
        },
        {
          icon: "people",
          title: "Membership Fees",
          value: cooperativeFinances?.data?.membership ?? 0,
        },
      ]);
    }
  }, [cooperativeFinances]);

  return (
    <>
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="finances-card-container flex flex-col gap-3">
          <NumberCard
            title={"Available Cooperative Balance"}
            value={cooperativeFinances?.data?.Wallet?.InAppBalance}
            icon="wallet"
            className="!bg-pry-dark-green-100"
            loading={fetchingFiancesSummary}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
            <Button
              text="Add funds"
              onClick={() => {
                handleModalOpen("fundCooperativeWalletModal");
              }}
              type="button"
              disabled={!hasPermissionsFor(["can.add.payment"], permissions)}
              icon={
                hasPermissionsFor(["can.add.payment"], permissions)
                  ? "plus"
                  : "lock"
              }
              variation="primary"
              size="medium"
            />

            <Button
              text="Request withdrawal"
              onClick={() => {
                handleModalOpen("cooperativeWithdrawalModal");
              }}
              type="button"
              disabled={!hasPermissionsFor(["can.add.payment"], permissions)}
              icon={
                hasPermissionsFor(["can.add.payment"], permissions)
                  ? "arrow-up"
                  : "lock"
              }
              variation="plain-3"
              size="medium"
            />
          </div>
        </div>

        <div className="finances-card-container flex flex-col gap-3">
          <NumberCard
            title={"Current Cooperative Balance"}
            value={cooperativeFinances?.data?.Wallet?.TotalBalance}
            icon="wallet"
            className="!bg-white !text-pry-green-200 border"
            iconClassName="!bg-light-green-bg-300"
            loading={fetchingFiancesSummary}
          />

          <p className="text-sm py-1 px-3 bg-light-green-bg-300 rounded-[4px]">
            <span className="font-medium text-pry-green-200">
              Current Cooperative Balance
            </span>{" "}
            reflects your real-time balance from all payments and transactions,
            both online and offline.
          </p>
        </div>
      </div>

      <InformationCard
        info={
          "ThriftCorp does not hold funds. All monies are held with licensed payment processors, custodians, or investment partners."
        }
        size="mini"
        variant="purple"
        className="mt-4"
        icon="secure"
        innerClassName="!items-center"
      />

      <BreakdownAccordion
        title="See This Month’s Inflows"
        tooltip="Breakdown of all online and offline inflow transactions in the current month."
        className="mt-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {financesBreakdown?.map((breakdown, idx) => {
            return (
              <NumberCard
                key={`${idx}`}
                title={breakdown.title}
                value={breakdown.value}
                className="!bg-white !text-pry-green-200 border !min-h-fit !max-w-full"
                iconClassName="!bg-light-green-bg-300"
                icon={breakdown.icon}
                titleClassName="!text-xs"
                valueClassName="!text-base"
                loading={fetchingFiancesSummary}
              />
            );
          })}
        </div>
      </BreakdownAccordion>

      <div className="flex flex-col gap-3 md:gap-6 mt-8">
        <div>
          <p className="text-base text-pry-dark-green-400 font-medium">
            Transaction History
          </p>
        </div>

        <TableComponent
          headers={[]}
          isRowClickable
          rowClassName="!py-3"
          rows={
            cooperativeTransactions?.data?.results?.map((transaction, idx) => {
              return {
                id: idx,
                onClick: () => {
                  setSelectedTransaction(transaction);
                  handleModalOpen("transactionDetailsModal");
                },
                items: [
                  <div className="flex flex-row gap-4 items-center">
                    <div className="flex flex-col gap-1 flex-1">
                      <p className="text-sm font-medium max-w-[80%] text-ellipsis overflow-hidden">
                        {
                          CooperativeTransactionWalletTypeMap[
                            transaction.wallettype
                          ].title
                        }
                      </p>
                      <p className="text-pry-dark-green-300/50 text-xs">
                        {transaction.flow_type === "in" ? "Credit" : "Debit"} ∙{" "}
                        {getTimeAgo(transaction.PaymentDate ?? "")}
                      </p>
                    </div>

                    <div className="flex flex-col gap-1 items-end">
                      <p
                        className={classNames(
                          "font-bold py-0.5 px-2 rounded-full",
                          {
                            "text-pry-green-300 bg-light-green-bg-500":
                              transaction.flow_type === "in",
                            "text-red-200 bg-light-red-bg-50":
                              transaction.flow_type === "out",
                          }
                        )}
                      >
                        {transaction.flow_type === "in" ? "+" : "-"}{" "}
                        {formatToCurrency(transaction.Amount, {
                          currency: "NGN",
                        })}
                      </p>
                    </div>
                  </div>,
                ],
              };
            }) ?? []
          }
          currentPage={getPageFromOffset(query.offset ?? 0, query.limit ?? 0)}
          isPaginated
          itemsPerPage={query?.limit}
          onPageChange={(page) => {
            setQuery({
              ...query,
              offset: getOffsetFromPage(page, query.limit ?? 0),
            });
          }}
          totalCount={cooperativeTransactions?.data?.count}
          isLoading={fetchingTransactions}
          loadingHeight={32}
          isTableEmpty={[0, undefined].includes(
            cooperativeTransactions?.data?.results?.length
          )}
          emptyTitle="No Transactions Found"
        />
      </div>

      {selectedTransaction ? (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          isModalOpen={modalOpen && modal === "transactionDetailsModal"}
          onModalClose={() => {
            handleModalClose();
            setSelectedTransaction(undefined);
          }}
          type={
            CooperativeTransactionWalletTypeMap[selectedTransaction.wallettype]
              .transactionCategory
          }
        />
      ) : undefined}

      <FundCooperativeWalletModal
        modal={modal}
        modalOpen={modalOpen}
        handleModalClose={handleModalClose}
        handleModalOpen={handleModalOpen}
        cooperativeDetails={cooperativeDetails}
        onSuccess={async () => {
          refetchSummary();
          refetchTransactions();
        }}
      />

      <CooperativeWithdrawalModal
        modal={modal}
        modalOpen={modalOpen}
        onModalClose={() => {
          handleModalClose();
        }}
        cooperativeDetails={cooperativeDetails}
        onSuccess={() => {
          handleModalOpen("withdrawalInitiationSuccessModal");
        }}
      />
    </>
  );
};
